"use client";

import React, { useEffect, useState } from "react";
import { Heading, Spinner, Text } from "ui-components";

const CONTINUE_LINK_DELAY_MS = 1000;

export interface IAutomaticApplyInterstitialProps {
  organizationName: string;
  applyUrl: string;
}

/**
 * Shown while a candidate is being handed off to the employer's application
 * form. Purely presentational - the caller owns the decision and the
 * navigation.
 *
 * Rendered only from client state, never on the server, so a candidate without
 * JavaScript gets the normal job page and the normal Apply button.
 */
export function AutomaticApplyInterstitial({
  organizationName,
  applyUrl,
}: IAutomaticApplyInterstitialProps): JSX.Element {
  const [isContinueLinkVisible, setIsContinueLinkVisible] = useState(false);

  useEffect(() => {
    // If navigation has not happened within a second, something has gone wrong.
    // Offer a real link so the candidate can finish the journey themselves
    // rather than being stranded on a spinner.
    const timeoutId = setTimeout(
      () => setIsContinueLinkVisible(true),
      CONTINUE_LINK_DELAY_MS
    );
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-6 bg-white px-6 text-center"
    >
      <Spinner intent="brand-primary" size="xl" />
      <Heading align="center">
        Taking you to {organizationName}&apos;s application form
      </Heading>
      <Text align="center">
        You are being redirected to apply for this role on {organizationName}
        &apos;s own website.
      </Text>
      {isContinueLinkVisible && (
        <a
          href={applyUrl}
          className="underline text-brand-primary font-semibold"
        >
          Continue to {organizationName}
        </a>
      )}
    </div>
  );
}
